import cytoscape from "cytoscape";
import dagre from "cytoscape-dagre";
import popper from "cytoscape-popper";
import { computePosition, flip, limitShift, shift } from "@floating-ui/dom";
import TrailblazerStyleConfig from "./CytoscapeConstants";

/** https://github.com/cytoscape/cytoscape.js-popper?tab=readme-ov-file */
function popperFactory(ref, content, opts) {
    // see https://floating-ui.com/docs/computePosition#options
    const popperOptions = {
        // matching the default behaviour from Popper@2
        // https://floating-ui.com/docs/migration#configure-middleware
        middleware: [
            flip(),
            shift({ limiter: limitShift() })
        ],
        ...opts
    };

    function update() {
        computePosition(ref, content, popperOptions).then(({ x, y }) => {
            Object.assign(content.style, {
                left: `${x}px`,
                top: `${y}px`
            });
        });
    }
    update();
    return { update };
}
cytoscape.use(dagre);
/** https://stackoverflow.com/questions/52085282/how-to-use-cytoscape-popper-extension-of-cytoscape-in-angular-6 */
cytoscape.use(popper(popperFactory));

export default class CytoscapeAdapter {
    constructor(containerRef, options = {}) {
        this.containerRef = containerRef;
        this.options = options;
        this.eventHandlers = {};
        this.cy = null;
        this.graphData = null;
        this.callbacks = {};
    }

    initialize() {
        if (!this.containerRef.current) return this;

        this.cy = cytoscape({
            container: this.containerRef.current,
            elements: [],
            style: TrailblazerStyleConfig.generateStylesheet(),
            ...TrailblazerStyleConfig.CYTOSCAPE_OPTIONS,
            ...this.options
        });

        return this;
    }

    destroy() {
        this.removeAllEventListeners();

        if (this.cy) {
            this.cy.destroy();
            this.cy = null;
        }
    }

    setElements(elements) {
        if (!this.cy) return;

        this.cy.elements().remove();
        this.cy.add(elements);

        const layout = this.cy.layout(TrailblazerStyleConfig.DAGRE_LAYOUT_OPTIONS);
        layout.run();
    }

    on(event, callback) {
        if (!this.cy) return;

        this.cy.on(event, callback);
        this.eventHandlers[event] = callback;
    }

    off(event) {
        if (!this.cy) return;

        if (event && this.eventHandlers[event]) {
            this.cy.off(event, this.eventHandlers[event]);
            delete this.eventHandlers[event];
        } else {
            Object.keys(this.eventHandlers).forEach((e) => {
                this.cy.off(e, this.eventHandlers[e]);
            });
            this.eventHandlers = {};
        }
    }

    setupInteractions(graphData, callbacks = {}) {
        this.graphData = graphData;
        this.callbacks = callbacks;
        this.setupEventListeners();
        return this;
    }

    setupEventListeners() {
        if (!this.cy) return;

        this.removeAllEventListeners();
        this.setupNodeInteractions();
        this.setupEdgeInteractions();
    }

    removeAllEventListeners() {
        if (this.cy) {
            this.cy.removeAllListeners();
        }
        this.eventHandlers = {};
    }

    setupNodeInteractions() {
        this.cy.on("tap", "node", (event) => {
            this.handleNodeTap(event);
        });

        this.cy.on("mouseover", "node", (event) => {
            this.handleNodeMouseOver(event);
        });

        this.cy.on("mouseout", "node", (event) => {
            this.handleNodeMouseOut(event);
        });
    }

    setupEdgeInteractions() {
        this.cy.on("mouseover", "edge", (event) => {
            this.handleEdgeMouseOver(event);
        });

        this.cy.on("mouseout", "edge", (event) => {
            this.handleEdgeMouseOut(event);
        });
    }

    handleNodeTap(event) {
        const { target: node } = event;
        const nodeId = node.id();
        console.log(nodeId);
    }

    handleNodeMouseOver(event) {
        const { cy, target: node } = event;
        const container = cy.container();
        if (container) {
            container.style.cursor = "pointer";
        }

        // tooltip element and styling
        const tooltip = document.createElement("div");
        tooltip.className = "node-tooltip";
        tooltip.style.background = "rgba(33, 33, 33, 0.9)";
        tooltip.style.color = "#ffffff";
        tooltip.style.padding = "12px 16px";
        tooltip.style.borderRadius = "8px";
        tooltip.style.fontSize = "13px";
        tooltip.style.fontFamily = "'Segoe UI', Roboto, -apple-system, BlinkMacSystemFont, Arial, sans-serif";
        tooltip.style.zIndex = "999";
        tooltip.style.pointerEvents = "none";
        tooltip.style.position = "absolute";
        tooltip.style.boxShadow = "0 4px 12px rgba(0, 0, 0, 0.15)";
        tooltip.style.maxWidth = "320px";
        tooltip.style.lineHeight = "1.5";
        tooltip.style.letterSpacing = "0.2px";
        tooltip.style.backdropFilter = "blur(4px)";
        tooltip.style.border = "1px solid rgba(255, 255, 255, 0.1)";
        tooltip.style.transition = "opacity 0.2s ease-in-out";

        // set content with the expandedLabel field,
        // convert newlines to HTML line breaks for formatting
        tooltip.innerHTML = node.data("expandedLabel").replace(/\n/g, "<br>");

        // Add to document body
        document.body.appendChild(tooltip);

        // create a popper tooltip if available
        if (node.popper) {
            node.popper({
                content: () => tooltip,
                popper: {
                    placement: "bottom",
                    modifiers: [
                        {
                            name: "offset",
                            options: {
                                offset: [0, 8]
                            }
                        }
                    ]
                }
            });

            // store popper instance on the node
            node.data("popper", popper);
        } else {
            // fallback positioning if popper is not available
            const updatePosition = () => {
                const renderedPosition = node.renderedPosition();
                const containerRect = container.getBoundingClientRect();

                tooltip.style.left = `${containerRect.left + renderedPosition.x}px`;
                tooltip.style.top = `${containerRect.top + renderedPosition.y + 10}px`;
            };

            // initial position
            updatePosition();

            // update position on pan/zoom/resize
            node.on("position.tooltip", updatePosition);
            cy.on("pan.tooltip zoom.tooltip resize.tooltip", updatePosition);
        }

        // store tooltip element reference
        node.data("tooltipElement", tooltip);

        // apply highlight style
        node.style({
            backgroundColor: "#ff7700",
            borderWidth: 2,
            borderColor: "#ff0000"
        });
    }

    handleNodeMouseOut(event) {
        const { cy, target: node } = event;
        const container = cy.container();

        if (container) {
            container.style.cursor = "default";
        }

        // get stored tooltip and popper references
        const tooltip = node.data("tooltipElement");
        const nodePopper = node.data("popper");

        // remove event listeners
        node.removeListener("position.tooltip");
        cy.removeListener("pan.tooltip");
        cy.removeListener("zoom.tooltip");
        cy.removeListener("resize.tooltip");

        // remove the tooltip element
        if (tooltip && tooltip.parentNode) {
            tooltip.parentNode.removeChild(tooltip);
        }

        // destroy popper instance if it exists
        if (nodePopper && typeof nodePopper.destroy === "function") {
            nodePopper.destroy();
        }

        // remove data references
        node.removeData("popper");
        node.removeData("tooltipElement");

        // reset node style
        node.style({
            backgroundColor: null,
            borderWidth: null,
            borderColor: null
        });
    }

    handleEdgeMouseOver(event) {
        const { cy, target: edge } = event;
        const container = cy.container();

        if (container) {
            container.style.cursor = "pointer";
        }

        edge.style({
            label: edge.data("label")
        });
    }

    handleEdgeMouseOut(event) {
        const { cy, target: edge } = event;
        const container = cy.container();

        if (container) {
            container.style.cursor = "default";
        }

        edge.style({
            label: ""
        });
    }

    updateGraphData(newGraphData) {
        this.graphData = newGraphData;
    }

    getCy() {
        return this.cy;
    }
}